import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatCOP, formatDate, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Disclaimer } from "../components/Disclaimer";
import PaymentCountdown from "../components/PaymentCountdown";
import ComprobanteUploadBlock from "../components/ComprobanteUploadBlock";
import CaseFlowStepper from "../components/CaseFlowStepper";
import CaseSummaryCard from "../components/CaseSummaryCard";
import FlowGuide from "../components/FlowGuide";
import ConfirmDialog from "../components/ConfirmDialog";
import { toast } from "sonner";
import { useN2Offer } from "../lib/useN2Offer";

const STATUS_STYLE = {
  pending: { label: "Pago pendiente", cls: "bg-amber-50 text-amber-900 border-amber-300" },
  confirmed: { label: "Pago confirmado", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  failed: { label: "Pago fallido", cls: "bg-rose-50 text-rose-800 border-rose-300" },
  refunded: { label: "Reembolsado", cls: "bg-gray-100 text-gray-700 border-gray-300" },
};

/**
 * Fase 11.2 — Pago Nivel 2 (Kit Documental).
 *
 * Esta página solo es accesible si el caso ya está en estado `nivel2_payment_pending`
 * y existe un intake N2. En esa condición, init payment es idempotente (devuelve pending|confirmed).
 */
export default function PaymentN2Page() {
  const n2Offer = useN2Offer();
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseDoc, setCaseDoc] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState(false);
  // Fase 12 Sprint 2: cuota gratuita N2
  const [quota, setQuota] = useState(null);
  const [quotaInitiating, setQuotaInitiating] = useState(false);
  // Iter24.c: modal de confirmación (reemplaza window.confirm)
  const [quotaConfirmOpen, setQuotaConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // RF-15 fix (iter27a): el backend es la fuente de verdad del estado.
      // Antes hacíamos un gate por state leído de /cases/{id}, que ante
      // lectura stale (replica lag o cache) producía un loop con /nivel2-request.
      // Ahora: primero intentamos GET del pago (si existe, pintarlo); si no
      // existe, intentamos init idempotente. Solo redirigimos a /nivel2-request
      // si el backend nos dice EXPLÍCITAMENTE que falta el intake.
      const [caseRes, quotaRes] = await Promise.all([
        api.get(`/cases/${id}`),
        api.get(`/me/quota`).catch(() => ({ data: null })),
      ]);
      setCaseDoc(caseRes.data);
      setQuota(quotaRes?.data || null);

      // Estados pre-N2 donde el pago realmente no aplica.
      const PRE_N2_STATES = new Set([
        "draft_intake", "nivel0_pending", "nivel0_ready", "out_of_scope",
        "nivel1_payment_pending", "nivel1_paid", "nivel1_processing",
        "nivel1_needs_human_review", "nivel1_revision_requested",
      ]);
      if (PRE_N2_STATES.has(caseRes.data.state)) {
        toast.info("Aún no puedes acceder al pago del Kit Documental.");
        navigate(`/cases/${id}`, { replace: true });
        return;
      }

      // Intentamos GET primero (si ya hay payment persistido, idempotente real).
      let paymentDoc = null;
      try {
        const { data: p0 } = await api.get(`/cases/${id}/payments/nivel2`);
        paymentDoc = p0;
      } catch (getErr) {
        // 404 = aún no existe → procedemos a init
        if (getErr?.response?.status !== 404) {
          if (process.env.NODE_ENV === "development") {
            console.warn("[PaymentN2Page] GET payments/nivel2 non-404 error:", getErr);
          }
        }
      }

      if (!paymentDoc) {
        try {
          const { data: p } = await api.post(`/cases/${id}/payments/nivel2/init`, {});
          paymentDoc = p;
        } catch (initErr) {
          const initDetail = String(initErr?.response?.data?.detail || "");
          const status = initErr?.response?.status;
          // Único caso en que mandamos de vuelta al intake: backend dice
          // explícitamente que falta el intake N2.
          if (status === 422 && /intake/i.test(initDetail) && !/pago ya fue confirmado/i.test(initDetail)) {
            toast.info("Primero completa la información del Kit Documental.");
            navigate(`/cases/${id}/nivel2-request`, { replace: true });
            return;
          }
          // Caso state-inválido (ej. nivel2_offer_sent sin transición): mostrar
          // error legible pero NO redirigir (evita loop). El usuario puede
          // volver manualmente al intake.
          if (status === 422) {
            setError(formatApiError(initDetail) || "El pago no se puede iniciar en el estado actual.");
            return;
          }
          setError(formatApiError(initDetail) || "No se pudo iniciar el pago");
          return;
        }
      }
      setPayment(paymentDoc);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "No se pudo cargar el caso");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Polling: si el pago está confirmed, monitorear estado del caso (variant=auto entrega rápido)
  useEffect(() => {
    if (!payment || payment.status !== "confirmed") return;
    if (!caseDoc) return;
    if (caseDoc.state === "nivel2_delivered") {
      navigate(`/cases/${id}`, { replace: true });
      return;
    }
    const t = setInterval(async () => {
      try {
        const { data: c } = await api.get(`/cases/${id}`);
        setCaseDoc(c);
        if (c.state === "nivel2_delivered") {
          clearInterval(t);
          toast.success("¡Tu Kit Documental está listo!");
          navigate(`/cases/${id}`, { replace: true });
        }
      } catch (pollErr) {
        // Errores transitorios de polling: no interrumpir UX, pero loggear en dev.
        if (process.env.NODE_ENV === "development") {
          console.warn("[PaymentN2Page] transient polling error:", pollErr);
        }
      }
    }, 3000);
    return () => clearInterval(t);
  }, [payment, caseDoc, id, navigate]);

  const retryPayment = async () => {
    setRetrying(true);
    try {
      const { data: p } = await api.post(`/cases/${id}/payments/nivel2/init`, {});
      setPayment(p);
      toast.success("Nuevo intento de pago iniciado");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo reiniciar el pago");
    } finally {
      setRetrying(false);
    }
  };

  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(payment?.reference || "");
      toast.success("Referencia copiada");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const useComplimentaryQuota = () => {
    if (!quota || (quota.kits_remaining ?? 0) <= 0) {
      toast.error("No tienes cuota gratuita disponible para Kits Documentales.");
      return;
    }
    setQuotaConfirmOpen(true);
  };

  const confirmUseComplimentaryQuota = async () => {
    setQuotaConfirmOpen(false);
    setQuotaInitiating(true);
    try {
      await api.post(`/cases/${id}/payments/nivel2/use-complimentary-quota`);
      toast.success("Cuota gratuita aplicada. Generando tu Kit Documental…");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo aplicar la cuota");
    } finally {
      setQuotaInitiating(false);
    }
  };

  if (loading) {
    return (
      <div className="container-prose py-12">
        <div className="border border-gray-200 p-8 text-sm text-gray-500">Cargando…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-prose py-12">
        <div className="max-w-2xl mx-auto">
          <Link to={`/cases/${id}`} className="text-sm text-gray-500 hover:text-primary">← Volver al caso</Link>
          <div className="mt-4 border border-destructive/30 bg-destructive/5 text-destructive p-4 text-sm" data-testid="payment-n2-error">{error}</div>
        </div>
      </div>
    );
  }

  if (!payment) return null;

  const st = STATUS_STYLE[payment.status] || STATUS_STYLE.pending;

  return (
    <div data-testid="payment-n2-page" className="container-prose py-12">
      <div className="max-w-3xl mx-auto">
        <div className="mb-2">
          <Link to={`/cases/${id}`} className="text-sm text-gray-500 hover:text-primary">← Volver al caso</Link>
        </div>

        {/* Iter20 — Summary + Stepper */}
        <CaseSummaryCard caseDoc={caseDoc} showLink={false} />
        <div className="mb-4">
          <CaseFlowStepper caseId={id} state={caseDoc?.state || "nivel2_payment_pending"} />
        </div>

        {/* Iter25 — Guía de auto-orientación N2 */}
        <div className="mb-6">
          <FlowGuide level="n2" quota={quota} state={caseDoc?.state} caseId={id} />
        </div>

        <div className="mb-8 pb-6 border-b border-gray-200">
          <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">
            Pago — Kit Documental
          </p>
          <h1 className="font-serif text-2xl text-gray-900 mb-3">Confirma tu pago</h1>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span data-testid={`payment-n2-status-${payment.status}`}
                  className={`uppercase tracking-widest border px-3 py-1 ${st.cls}`}>
              {st.label}
            </span>
            <span className="text-gray-500">Valor estimado del proceso: {formatCOP(caseDoc?.valor_estimado_cop)}</span>
          </div>
        </div>

        {/* Variant elegido (informativo) */}
        {caseDoc?.variant_nivel2 && (
          <div
            className={`border p-4 mb-6 ${caseDoc.variant_nivel2 === "auto" ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`}
            data-testid="variant-n2-banner"
          >
            <p className="text-sm font-medium text-gray-900">
              Plan elegido: {caseDoc.variant_nivel2 === "auto" ? "Kit Documental Automatizado (sin revisión humana)" : "Kit Documental con revisión humana experta"}
              {" — "}
              {formatCOP(caseDoc.variant_nivel2 === "auto" ? n2Offer.price_auto_cop : n2Offer.price_reviewed_cop)}
            </p>
            {caseDoc.variant_nivel2 === "auto" && (
              <p className="text-xs text-amber-900 mt-1">
                Sin revisión humana — el paquete se descargará inmediatamente tras confirmarse el pago.
                La responsabilidad de su uso es del usuario.
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-gray-200 bg-white mb-6">
          <div className="p-6 md:border-r border-gray-200">
            <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">Monto a pagar</p>
            <p data-testid="payment-n2-amount" className="font-serif text-3xl text-gray-900">{formatCOP(payment.amount_cop)}</p>
            <p className="text-xs text-gray-500 mt-1">IVA y comisiones incluidos.</p>
          </div>
          <div className="p-6">
            <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">Referencia</p>
            <div className="flex items-center gap-3">
              <code data-testid="payment-n2-reference" className="font-mono text-sm bg-gray-100 px-2 py-1 border border-gray-300">{payment.reference}</code>
              <Button size="sm" variant="outline" className="rounded-sm border-gray-300 h-8 text-xs" onClick={copyRef} data-testid="copy-reference-n2">Copiar</Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">Incluye esta referencia en el concepto del pago.</p>
          </div>
        </div>

        {payment.status === "pending" && (
          <div data-testid="payment-n2-instructions" className="border border-gray-200 bg-white p-7 mb-6">
            <h2 className="font-serif text-xl text-gray-900 mb-4">Cómo pagar</h2>

            {/* Fase 12 Sprint 2: Cuota gratuita Kit (si admin asignó >0) */}
            {quota && (quota.kits_remaining ?? 0) > 0 && (
              <div className="border-2 border-emerald-300 bg-emerald-50/40 p-5 mb-5" data-testid="quota-pay-block-n2">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <p className="text-[10px] uppercase tracking-widest text-emerald-800 font-medium mb-1">
                      Cuota gratuita disponible
                    </p>
                    <h3 className="font-serif text-lg text-gray-900 mb-1">
                      Usar 1 Kit Documental gratuito
                    </h3>
                    <p className="text-sm text-gray-700">
                      Te quedan <strong>{quota.kits_remaining}</strong> de {quota.kits_limit} cuotas gratuitas
                      de Kit Documental este mes ({quota.year_month}).
                    </p>
                  </div>
                  <Button
                    onClick={useComplimentaryQuota}
                    disabled={quotaInitiating}
                    className="rounded-sm h-12 px-6 bg-emerald-700 hover:bg-emerald-800"
                    data-testid="quota-pay-button-n2"
                  >
                    {quotaInitiating ? "Aplicando…" : "Usar cuota gratuita"}
                  </Button>
                </div>
              </div>
            )}

            {/* Manual payment options — show ALL available methods */}
            <div className="space-y-4">
              {/* Bank transfer */}
              {payment.bank_transfer_info && (
                <div data-testid="provider-bank-transfer-n2" className="border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Transferencia bancaria</h4>
                  <table className="w-full text-sm border border-gray-200 bg-white">
                    <tbody>
                      {Object.entries(payment.bank_transfer_info).map(([k, v]) => (
                        <tr key={k} className="border-b border-gray-200 last:border-b-0">
                          <td className="px-4 py-2 text-xs uppercase tracking-widest text-gray-500 bg-gray-50 w-1/3">{k}</td>
                          <td className="px-4 py-2 text-gray-800">{String(v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-sm text-gray-700 mt-3">
                    Realiza la transferencia con la referencia <code className="font-mono">{payment.reference}</code>.
                  </p>
                </div>
              )}

              {/* QR Bre-B (generado dinámicamente desde llave configurada) */}
              {(payment.qr_data_url || payment.qr_image_url) && (
                <div data-testid="provider-qr-n2" className="border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">QR Bre-B</h4>
                  <img
                    src={payment.qr_data_url || payment.qr_image_url}
                    alt="QR Bre-B"
                    className="max-w-[280px] border border-gray-200 bg-white"
                  />
                  {payment.bre_b_key && (
                    <p className="text-xs text-gray-600 mt-2">
                      Llave Bre-B: <code className="font-mono">{payment.bre_b_key}</code>
                    </p>
                  )}
                  <p className="text-sm text-gray-700 mt-3">
                    Escanea el QR con tu app bancaria Bre-B. Incluye la referencia <code className="font-mono">{payment.reference}</code>.
                  </p>
                </div>
              )}

              {/* External payment link */}
              {payment.payment_link && (
                <div data-testid="provider-external-link-n2" className="border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Pasarela externa</h4>
                  <Button asChild className="rounded-sm h-12 px-8" data-testid="external-pay-button-n2">
                    <a href={payment.payment_link} target="_blank" rel="noopener noreferrer">Pagar ahora</a>
                  </Button>
                  <p className="text-xs text-gray-500 mt-3">
                    Se abrirá una nueva pestaña. Al completar el pago, espera la confirmación. Procesamos pagos en máx. 24 horas hábiles.
                  </p>
                </div>
              )}

              {/* Extra instructions */}
              {payment.extra_instructions && (
                <div data-testid="provider-extra-instructions-n2" className="border border-gray-200 bg-gray-50 p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Instrucciones adicionales</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{payment.extra_instructions}</p>
                </div>
              )}

              {/* Fallback: contact email (always shown) */}
              <div data-testid="provider-manual-n2" className="border border-gray-200 bg-gray-50 p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Otra forma de pago</h4>
                <p className="text-sm text-gray-700 mb-4">
                  Para coordinar el pago, escribe a{" "}
                  <a className="text-primary underline" href={`mailto:${payment.admin_contact_email}?subject=Pago Kit Documental referencia ${payment.reference}&body=Hola,%0A%0ASolicito coordinación de pago Kit Documental para la referencia ${payment.reference}.%0A%0AGracias.`}>
                    {payment.admin_contact_email}
                  </a>{" "}
                  con la referencia <code className="font-mono">{payment.reference}</code>.
                </p>
                <Button asChild className="rounded-sm" data-testid="contact-mailto-n2">
                  <a href={`mailto:${payment.admin_contact_email}?subject=Pago Kit Documental referencia ${payment.reference}`}>Contactar a operaciones</a>
                </Button>
              </div>

              {/* P1: Subir comprobante de pago (Bre-B / transferencia) */}
              <ComprobanteUploadBlock
                caseId={id}
                nivel="nivel2"
                paymentStatus={payment.status}
                alreadyUploaded={Boolean(payment.comprobante_url || payment.comprobante_uploaded_at)}
                uploadedAt={payment.comprobante_uploaded_at}
                filename={payment.comprobante_filename}
                onUploaded={load}
              />
            </div>
          </div>
        )}

        {payment.status === "pending" && (
          <>
            <PaymentCountdown level="nivel2" testid="payment-countdown-n2" />
            <div className="border border-gray-200 bg-gray-50 p-6 mb-6">
              <h3 className="font-serif text-lg text-gray-900 mb-2">Tras tu pago</h3>
              <p className="text-sm text-gray-700">
                Una vez confirmado el pago, iniciamos la generación del Kit Documental. Todo es revisado por experto humano antes de la descarga.
              </p>
            </div>
          </>
        )}

        {payment.status === "confirmed" && (
          <div data-testid="payment-n2-confirmed-block" className="border border-emerald-200 bg-emerald-50/40 p-6 mb-6">
            <h3 className="font-serif text-lg text-emerald-900 mb-2">Pago confirmado</h3>
            <p className="text-sm text-emerald-900">
              Tu Kit Documental entró en cola de generación. Te notificaremos cuando esté listo para revisión humana.
            </p>
            {payment.confirmed_at && (
              <p className="text-xs text-emerald-800/80 mt-2">Confirmado el {formatDate(payment.confirmed_at)}</p>
            )}
          </div>
        )}

        {payment.status === "failed" && (
          <div data-testid="payment-n2-failed-block" className="border border-rose-200 bg-rose-50/40 p-6 mb-6">
            <h3 className="font-serif text-lg text-rose-900 mb-2">Pago fallido</h3>
            {payment.fail_reason && <p className="text-sm text-rose-900 mb-4">{payment.fail_reason}</p>}
            <Button onClick={retryPayment} disabled={retrying} className="rounded-sm" data-testid="payment-n2-retry">
              {retrying ? "Reintentando…" : "Reintentar pago"}
            </Button>
          </div>
        )}

        <div className="mt-8">
          <Disclaimer dataTestId="payment-n2-disclaimer" />
          <p className="mt-3 text-xs text-gray-500 italic">
            La entrega del Kit Documental requiere confirmación humana del pago y posterior revisión humana experta antes de la descarga. Insumo de apoyo. No reemplaza el concepto de un abogado titulado.
          </p>
        </div>
      </div>
      <ConfirmDialog
        open={quotaConfirmOpen}
        onOpenChange={setQuotaConfirmOpen}
        title="Usar cuota gratuita"
        description={
          <>
            Vas a usar <strong>1</strong> de tus{" "}
            <strong>{quota?.kits_remaining ?? 0}</strong> cuotas gratuitas de
            <strong> Kit Documental</strong> de este mes. La cuota se descontará
            inmediatamente y se generará el Kit Documental Nivel 2.
            <br />
            <br />
            ¿Deseas continuar?
          </>
        }
        confirmLabel="Sí, usar cuota"
        cancelLabel="Cancelar"
        onConfirm={confirmUseComplimentaryQuota}
        loading={quotaInitiating}
        testIdPrefix="quota-confirm-n2"
      />
    </div>
  );
}
